import { t } from "i18next";
import _ from "lodash";
import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useFetch } from "../../../../../hooks/useFetch";
import { UserCompact } from "../../../../../types/types";
import { Label } from "../../../../../ui/Label/Label";
import { SelectButton } from "../../../../../ui/SelectButton/SelectButton";
import { ScrollArea } from "../../../../../ui/ScrollArea/ScrollArea";
import { Separator } from "../../../../../ui/Separator";
import { Button } from "../../../../../ui/Button";
import DeleteUser from "../../DeleteUser";
import { Input } from "../../../../../ui/Input/Input";
import { useAppStore } from "../../../../../stores/RootStore";
import { userStatuses } from "../../../../../enums";
import { useOutletContext } from "react-router-dom";

type Props = {
  id: string | undefined;
  currentUser: UserCompact | undefined;
};

const UserSettingsTab = ({ id, currentUser }: Props) => {
  const [users, setUsers] = useOutletContext<[UserCompact[], Function]>();

  const divisions = useAppStore((store) => store.divisions);
  const personneles = useAppStore((store) => store.persons);
  const divisionsList = divisions?.map((division) => {
    return { value: division.id, label: division.attributes.name };
  });
  const personnelesList = personneles?.map((person) => {
    return { value: person.id, label: `${person.attributes.last_name} ${person.attributes.first_name} ${person.attributes.middle_name}` };
  });

  let [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const { handleSubmit, reset, control } = useForm({
    defaultValues: {
      first_name: currentUser?.attributes.first_name ? currentUser?.attributes.first_name : "",
      last_name: currentUser?.attributes.last_name ? currentUser?.attributes.last_name : "",
      email: currentUser?.attributes.email,
      status: userStatuses.find((status) => status.value == currentUser?.attributes.status),
      division: divisionsList?.find((division) => division.value == String(currentUser?.attributes.division_id)),
      personnel: personnelesList?.find((person) => person.value == String(currentUser?.attributes.personnel_id))
    },
  });

  function openDeleteModal() {
    setIsDeleteOpen(true);
  }

  function closeDeleteModal() {
    setIsDeleteOpen(false);
  }

  // Обновление информации об участнике
  const [{ response: saveResponse, isLoading: saveIsLoading, error: saveError }, doFetchItemSave] = useFetch(
    `/users/${id}`
  );

  // Баг на бэке с редактированием
  const onSubmit = (formData: any) => {
    doFetchItemSave({
      method: "PUT",
      data: {
        data: {
          first_name: formData.first_name,
          last_name: formData.last_name,
          status: formData.status?.value ? formData.status.value : null,
          division_id: formData.division?.value ? formData.division.value : null,
          personnel_id: formData.personnel?.value ? formData.personnel.value : null,
        },
      },
    });
  };

  const editUser = (itemSaveResponse: { data: unknown }) => {
    const newUserData = itemSaveResponse.data as unknown as UserCompact;
    const withoutCurrentItem = users?.filter((user) => user.id != currentUser?.id)!;
    setUsers([...withoutCurrentItem, newUserData]);
  };

  useEffect(() => {
    if (!saveResponse) return;
    editUser(saveResponse);
  }, [saveResponse]);

  return (
    <>
      <ScrollArea className="w-full">
        <div className="flex flex-col w-full">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="flex flex-col gap-3">
              <Controller
                name="first_name"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <div className="relative space-y-2">
                    <Input {...field} label={`${t("name")}*`} />
                  </div>
                )}
              />
              <Controller
                name="last_name"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <div className="relative space-y-2">
                    <Input {...field} label={`${t("surname")}*`} />
                  </div>
                )}
              />
              <Controller
                name="email"
                control={control}
                disabled
                render={({ field }) => (
                  <div className="relative space-y-2">
                    <Input {...field} label={`${t("email")}*`} />
                  </div>
                )}
              />
              <Controller
                name="status"
                control={control}
                rules={{ required: true }}
                render={({ field: { value, onChange } }) => (
                  <div className="relative space-y-2">
                    <Label>{t("status")}*</Label>
                    <SelectButton
                      label={"Выберите роль"}
                      searchable={false}
                      options={userStatuses}
                      value={value ? userStatuses.find((s) => s.value == String(value.value))! : null}
                      onChange={onChange}
                    />
                  </div>
                )}
              />
              <Controller
                name="division"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <div className="relative space-y-2">
                    <Label>{t("division")}</Label>
                    <SelectButton
                      label={"Выберите подразделение"}
                      searchable={true}
                      options={divisionsList!}
                      value={value ? divisionsList?.find((s) => s.value == String(value.value))! : null}
                      onChange={onChange}
                    />
                  </div>
                )}
              />
              <Controller
                name="personnel"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <div className="relative space-y-2">
                    <Label>{t("personnel_one")}</Label>
                    <SelectButton
                      label={"Выберите сотрудника"}
                      searchable={true}
                      options={personnelesList!}
                      value={value ? personnelesList?.find((s) => s.value == String(value.value))! : null}
                      onChange={onChange}
                    />
                  </div>
                )}
              />
              <div className="mt-8 flex w-full justify-between">
                <Button variant={"destructive"} size={"sm"} type="button" onClick={() => openDeleteModal()}>
                  {t("delete")}
                </Button>
                <Button variant={"default"} size={"sm"} type="submit">
                  {t("save")}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </ScrollArea>
      {isDeleteOpen && <DeleteUser id={id!} isDeleteOpen={isDeleteOpen} closeModal={closeDeleteModal} />}
    </>
  );
};

export default UserSettingsTab;
